import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { useAppStore } from "@/global/store";
import { getPackagesApi } from "@/global/api/events";
import { useAppSystem } from "@/global/hooks/useAppSystem";
import { useCartHelpers } from "../useCartHelpers";
import {
  packagesState,
  selectPackages,
  selectPackageSelected,
} from "@/global/store/data/purchaseSeats/packages";
import { selectUser } from "@/global/store/data/user";
import {
  authUIDs,
  purchaseSeatsTabUIDs,
  vendorUUIDs,
} from "@/global/config/constants";
import { eventsState } from "@/global/store/data/purchaseSeats/events";
import { getEventsBelowMaxLimit } from "../utils.eventValidators";

import type { EventPackage, EventType } from "@/global/store/data/types";
import { routes } from "@/global/routes";
import { useTicketmaster } from "@/global/hooks/useTicketmaster";
import { useInternalAuth } from "@/global/hooks/useInternalAuth";
import { useTicketsdotcom } from "@/global/hooks/useTicketsdotcom";
import { useFraudPrevention } from "@/global/context/FraudPreventionProvider";
import { useGlobalApis } from "@/global/hooks/useGlobalApis";
import { handleSetUpdatedAccountGroup } from "../Packs/packs.utils";

export function useEventPackages() {
  // systems
  const { teamConfig, dispatch, router } = useAppSystem();
  const {
    platform,
    teamSchema,
    packageConfigs,
    shouldUseInternalLogin,
    dynamicPricingForPackages,
    refreshInventoryWhenEventClick,
  } = teamConfig;

  // apis
  const getPackages = useMutation(getPackagesApi);

  // states
  const user = useAppStore(selectUser);
  const packages = useAppStore(selectPackages);
  const [isLoading, setIsLoading] = useState(false);
  const selectedPackage = useAppStore(selectPackageSelected);
  const urlSelectedPkgId = Number(router.query.selectedPackageId);

  const { handleConnectTm } = useTicketmaster();
  const { handleInternalRedirect } = useInternalAuth();
  const { handleLoginOkta } = useTicketsdotcom();
  const { handleCheckBrokerEmail } = useFraudPrevention();
  const { switchPurchaseSeatTab } = useCartHelpers();
  const { handleRefreshEvent } = useGlobalApis();

  // prettier-ignore
  const handleConfirmGameSelection = async (args: {
    eventsSelected: EventType[];
    packageSelected: EventPackage | null;
  }) => {
    setIsLoading(true);
    if (!user) {
      let query: any = {
        teamSchema,
        purchaseSeatTab: purchaseSeatsTabUIDs.chooseSeats,
        eventCodes: args?.eventsSelected?.map((itm: any) => itm.eventCode).join("-"),
      };
      if (args?.packageSelected) query.packageId = args?.packageSelected?.id;

      let state = {
        authOrigin: "purchaseSeats",
        pathname: routes.purchaseSeats,
        query,
      };

      if (platform == vendorUUIDs.ticketmaster) {
        handleConnectTm(state);
      }

      if (platform == vendorUUIDs.tickesdotcom) {
        if (shouldUseInternalLogin) {
          const callback = window.btoa(JSON.stringify(state));
          return await handleInternalRedirect(
            routes.auth,
            callback,
            authUIDs.login
          );
        }

        await handleLoginOkta(state);
      }
    } else {
      // check if user is a broker
      const isBroker = await handleCheckBrokerEmail();
      if (isBroker) return;
      await switchPurchaseSeatTab(purchaseSeatsTabUIDs.chooseSeats, args?.eventsSelected[0]?.eventCode);
    }
    setIsLoading(false);
  };

  // prettier-ignore
  const handleSelectPackage = async (pkg: EventPackage) => {
    const pkgEvents = pkg.isBuildYourOwn ? [] : pkg.events;
    const pkgEventsBelowMaxLimit = getEventsBelowMaxLimit(pkgEvents, teamConfig);
    const { payload : packageSelected } =  await dispatch(packagesState.actions.setPackageSelected(pkg));
    const { payload : eventsSelected } = await dispatch(eventsState.actions.setEventsSelected(pkgEventsBelowMaxLimit));

    // getting most updated inventory if event is selected and config is set
    if (eventsSelected && platform === vendorUUIDs.tickesdotcom && refreshInventoryWhenEventClick) {
      await Promise.all(eventsSelected.map((event: EventType) => handleRefreshEvent(event.eventCode)));
    }

    // performing router operation and cleaning up unnecessary params
    const routerQuery = { ...router.query };
    if (urlSelectedPkgId) delete routerQuery.selectedPackageId;
    routerQuery.purchaseSeatTab = purchaseSeatsTabUIDs.findGames;
    if (dynamicPricingForPackages?.enabled) {
      const dynamicPricing = dynamicPricingForPackages.list.find((item) => item.id == pkg.id);
      if (!!dynamicPricing) handleSetUpdatedAccountGroup([String(dynamicPricing.updatedAccountGroup)], teamSchema);
    }
    if ((packages && packages.length == 1) || packageConfigs?.cantSelectMoreGames)
      return await handleConfirmGameSelection({eventsSelected, packageSelected});
    if (!pkg.isBuildYourOwn) routerQuery.isPkgConfirmOpen = String(true);
    router.push({ query: routerQuery });
  };

  const handleGetPackages = async () => {
    try {
      let payload = { teamSchema, userId: user?.userId };
      await getPackages.mutateAsync(payload, {
        onSuccess: async ({ data }: { data: EventPackage[] }) => {
          dispatch(packagesState.actions.setPackages(data));

          // checking and selecting pkg from url
          if (urlSelectedPkgId) {
            let pkgToSelect = data.find((itm) => itm.id == urlSelectedPkgId);
            if (pkgToSelect && !pkgToSelect.isBuildYourOwn) {
              handleSelectPackage(pkgToSelect);
            }
          }
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  return {
    apis: { getPackages },
    handlers: { handleSelectPackage, handleGetPackages },
    states: { packages, isLoading, selectedPackage },
  };
}
